import React from 'react';
import { type ColumnDef } from '@/components/organism/Table';
import { Cell, PillCell } from '@/components/atoms';
import { CRON_JOBS_TRACKER_PROPS } from '@/constant/cronJobsTracker/cronJobsTracker.constants';
import { tid } from '@/utils/testId';
import { CRON_JOBS_TEST_IDS as TID } from '@/constant/testIds/cronJobs.testIds';
import { getDateTime, getDateTimeAgo } from '@/utils/dateTime';
import { ICronJobTrackerProps } from './cronJobsTracker.interface';

/* ── Column definitions ───────────────────────────────────────────────────── */
export function buildColumns(
  isCopy: string,
  onCopyClick: (key: string) => void,
): ColumnDef<ICronJobTrackerProps>[] {
  const makeCopy = (copyKey: string) => ({
    copyKey,
    isCopy,
    onCopyClick,
  });

  return [
    {
      accessor: 'requestId',
      header: CRON_JOBS_TRACKER_PROPS.COLUMNS.REQUEST_ID,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_REQUEST_ID(`${idx}`))}
          value={item.requestId}
          tooltip
          variant="mono"
          size="text-[11px]"
          copy={makeCopy(`reqid-${item.cronJobId}`)}
        />
      ),
    },
    {
      accessor: 'jobName',
      header: CRON_JOBS_TRACKER_PROPS.COLUMNS.JOB_NAME,
      sorting: true,
      render: (item, idx) =>
        item.jobName ? (
          <PillCell
            testId={tid(TID.COL_JOB_NAME(`${idx}`))}
            value={item.jobName}
            config={{
              bg: 'bg-gray-100 dark:bg-gray-800/60',
              text: 'text-gray-600 dark:text-gray-400',
              border: 'border-gray-200 dark:border-gray-700',
            }}
            shape="badge"
            size="text-[11px]"
            className="font-semibold border"
          />
        ) : (
          <Cell value={undefined} variant="muted" />
        ),
    },
    {
      accessor: 'status',
      header: CRON_JOBS_TRACKER_PROPS.COLUMNS.STATUS,
      render: (item, idx) => (
        <PillCell
          testId={tid(TID.COL_STATUS(`${idx}`))}
          value={item.status}
          preset="cronStatus"
          dot
          className="border font-semibold"
        />
      ),
    },
    {
      accessor: 'startTime',
      header: CRON_JOBS_TRACKER_PROPS.COLUMNS.START_TIME,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_START_TIME(`${idx}`))}
          value={item.startTime}
          format="datetime"
          variant="muted"
          size="text-[11px]"
        />
      ),
    },
    {
      accessor: 'endTime',
      header: CRON_JOBS_TRACKER_PROPS.COLUMNS.END_TIME,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_END_TIME(`${idx}`))}
          value={item.endTime}
          format="datetime"
          variant="muted"
          size="text-[11px]"
        />
      ),
    },
    {
      accessor: 'updatedOn',
      header: CRON_JOBS_TRACKER_PROPS.COLUMNS.UPDATED_ON,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_UPDATED_ON(`${idx}`))}
          value={item.updatedOn}
          variant="muted"
          size="text-[11px]"
          tooltip={getDateTime(item.updatedOn)}
          tooltipPlacement="top"
        >
          {item.updatedOn ? getDateTimeAgo(item.updatedOn) : '—'}
        </Cell>
      ),
    },
    {
      accessor: 'createdOn',
      header: CRON_JOBS_TRACKER_PROPS.COLUMNS.CREATED_ON,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_CREATED_ON(`${idx}`))}
          value={item.createdOn}
          format="datetime"
          variant="muted"
          size="text-[11px]"
        />
      ),
    },
  ];
}
